import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useCallback } from 'react'
import { Gift, Clock, CheckCircle, XCircle, Plus } from 'lucide-react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { PageTitle } from '@/shared/ui/page-title'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { useMyGiftCards } from '@/features/gift-cards/api/use-my-gift-cards'
import { formatPrice } from '@/shared/lib/format'
import { cn } from '@/shared/lib/utils'

export const Route = createFileRoute('/my-gift-cards')({
  component: MyGiftCardsPage,
})

const STATUS_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  pending: { label: 'Ожидает оплаты', icon: Clock, color: 'text-amber-500' },
  active: { label: 'Активен', icon: CheckCircle, color: 'text-teal-600' },
  used: { label: 'Использован', icon: CheckCircle, color: 'text-gray-400' },
  expired: { label: 'Истёк', icon: XCircle, color: 'text-gray-400' },
  cancelled: { label: 'Отменён', icon: XCircle, color: 'text-red-500' },
}

function MyGiftCardsPage() {
  const navigate = useNavigate()
  const handleBack = useCallback(() => {
    if (window.history.length > 1) window.history.back()
    else navigate({ to: '/profile' })
  }, [navigate])
  useBackButton(handleBack)

  const { data: cards, isLoading } = useMyGiftCards()

  return (
    <AppShell showBottomNav>
      <div className="flex-1 bg-[var(--tg-theme-bg-color,#FFFFFF)] pb-24 overflow-y-auto">
        <div className="px-4 pt-4 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-primary" />
              </div>
              <PageTitle title="Мои сертификаты" subtitle="История покупок" />
            </div>
            <Link to="/gift-cards">
              <button className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </button>
            </Link>
          </div>
        </div>

        <div className="px-4 space-y-3">
          {isLoading && (
            <>
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color,#F5F5F5)] border border-[var(--tg-theme-hint-color,#999)]/15">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </>
          )}

          {!isLoading && (!cards || cards.length === 0) && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[var(--tg-theme-secondary-bg-color,#F5F5F5)] flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-[var(--tg-theme-hint-color,#999)]" />
              </div>
              <p className="text-sm font-medium text-[var(--tg-theme-text-color,#1A1A1A)] mb-1">Нет сертификатов</p>
              <p className="text-xs text-[var(--tg-theme-hint-color,#999)] mb-4">Подарите чай близким</p>
              <Link to="/gift-cards">
                <Button variant="primary" size="md">
                  <Gift className="w-4 h-4 mr-1.5" />
                  Купить сертификат
                </Button>
              </Link>
            </div>
          )}

          {cards?.map(card => {
            const statusCfg = STATUS_CONFIG[card.status] || STATUS_CONFIG.pending
            const StatusIcon = statusCfg.icon
            const usedPercent = card.initialAmount > 0
              ? Math.round(((card.initialAmount - card.balanceAmount) / card.initialAmount) * 100)
              : 0

            return (
              <div
                key={card.id}
                className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color,#F5F5F5)] border border-[var(--tg-theme-hint-color,#999)]/15"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-lg font-bold text-[var(--tg-theme-text-color,#1A1A1A)]">
                      {formatPrice(card.initialAmount)}
                    </p>
                    {card.recipientName && (
                      <p className="text-xs text-[var(--tg-theme-hint-color,#999)]">
                        Для: {card.recipientName}
                      </p>
                    )}
                    {card.recipientEmail && !card.recipientName && (
                      <p className="text-xs text-[var(--tg-theme-hint-color,#999)]">
                        {card.recipientEmail}
                      </p>
                    )}
                  </div>
                  <div className={cn('flex items-center gap-1 text-xs font-medium', statusCfg.color)}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusCfg.label}
                  </div>
                </div>

                {/* Progress bar для активных/использованных */}
                {(card.status === 'active' || card.status === 'used') && usedPercent > 0 && (
                  <div className="mb-2">
                    <div className="h-1.5 rounded-full bg-[var(--tg-theme-hint-color,#999)]/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-teal-500 transition-all"
                        style={{ width: `${usedPercent}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-[var(--tg-theme-hint-color,#999)] mt-1">
                      Использовано {usedPercent}% · Остаток: {formatPrice(card.balanceAmount)}
                    </p>
                  </div>
                )}

                <p className="text-[10px] text-[var(--tg-theme-hint-color,#999)]">
                  {new Date(card.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {card.expiresAt && ` · до ${new Date(card.expiresAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                </p>
              </div>
            )
          })}
        </div>
      </div>
      <BottomNav />
    </AppShell>
  )
}
